




















































































import { Component, Prop, Model, Vue } from 'vue-property-decorator'
/**
 * @displayName Oz Input
 */
// set inheritAttrs so the values of $attrs intended for the inner input element (e.g. required, type, placeholder) won't be set for the div wrapper
// Reference: https://vuejs.org/v2/guide/components-props.html#Disabling-Attribute-Inheritance
// In Vue 3 $attrs will include class and style so things may break: https://v3.vuejs.org/guide/migration/attrs-includes-class-style.html#overview
// Can be fixed by following this: https://github.com/vuejs/vue/issues/6144#issue-243557652
@Component({ inheritAttrs: false })
export default class OzInput extends Vue {
  $refs!: {
    inputElement: HTMLInputElement
  }

  /**
   * Dark mode.
   */
  @Prop({ default: 'auto' }) darkMode!: true | false | 'auto'

  /**
   * Changes border color if there are any errors
   * @values yes, no, unknown
   */
  @Prop({ default: 'unknown' }) valid!: 'yes' | 'no' | 'unknown'

  /**
   * Determines if input is disabled
   * @default false
   */
  @Prop({ default: false }) disabled?: boolean

  /**
   * Used to query the input element for testing purposes
   */
  @Prop({ default: '' }) testId?: string

  /**
   * Display a message after validating the input value
   */
  @Prop({ default: '' }) validationMessage?: string

  isFocused = false

  // this will be used if v-model is not passed to the component
  internalInputValue = ''

  /**
   * v-model will be binded to bindedInputValue and updated using the event update-v-model
   * Note:
   *  - Don't add a prop named bindedInputValue or emit update-v-model in this component to avoid conflict
   *  - `inputValue` doesn't get updated on every keypress on android when using `v-model`, so use `binded-input-value` and `@input` to fix it (see OrgVerificationApp.vue)
   *    @see https://github.com/vuejs/vue/issues/9777#issuecomment-478831263
   */
  @Model('update-v-model') readonly bindedInputValue!: string

  get inputValue(): string {
    return this.bindedInputValue === undefined ? this.internalInputValue : this.bindedInputValue
  }

  set inputValue(value: string) {
    this.internalInputValue = value
    /**
     * Trigger to update value binded using v-model
     * @property {string} value
     */
    this.$emit('update-v-model', value)
  }

  get isValid(): boolean {
    return this.valid !== 'no' ? true : false
  }

  get xValidationMessage(): boolean {
    return !this.isValid && !!this.validationMessage?.length
  }

  get hasExtraContent(): boolean {
    return !!this.$slots.extraContent
  }

  get borderClasses(): string | Record<string, boolean> {
    if (!this.isValid) return 'border-danger-100'

    if (this.disabled)
      return {
        'border-grey-300': this.darkMode === false,
        'border-white-disabled': this.darkMode === true,
        'border-grey-300 dark:border-white-disabled': this.darkMode === 'auto',
      }

    if (this.isFocused)
      return {
        'border-grape-500': this.darkMode === false,
        'border-canary-500': this.darkMode === true,
        'border-grape-500 dark:border-canary-500': this.darkMode === 'auto',
      }

    return {
      'border-dark-ui-100': this.darkMode === false,
      'border-light-ui-100': this.darkMode === true,
      'border-dark-ui-100 dark:border-light-ui-100': this.darkMode === 'auto',
    }
  }

  focusin(event: FocusEvent): void {
    this.isFocused = true
    /**
     * Emit the focusin event again after we are done with our internal handling
     * So the user of the component can listen to it if they want
     * @property {FocusEvent} event
     */
    this.$emit('focusin', event)
  }

  focusout(event: FocusEvent): void {
    this.isFocused = false
    /**
     * Emit the focusout event again after we are done with our internal handling
     * So the user of the component can listen to it if they want
     * @property {FocusEvent} event
     */
    this.$emit('focusout', event)
  }

  blur(event: FocusEvent): void {
    /**
     * Emit the blur event again after we are done with our internal handling
     * So the user of the component can listen to it if they want
     * @property {BlurEvent} event
     */
    this.$emit('blur', event)
  }

  focusInput(): void {
    this.$refs.inputElement.focus()
  }

  blurInput(): void {
    this.$refs.inputElement.blur()
  }
}
